import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listScans,
  scanFacets,
  type ScanFacets,
  type ScanListItem,
  type ScanListParams,
  type ScanSort,
} from "../../../../lib/siteScan";

/**
 * The scans table's state: filters, sorting, paging and the two requests that
 * feed it.
 *
 * Search is debounced because it is typed; everything else applies at once,
 * because a checkbox is a decision and waiting 300 ms for it feels broken. Any
 * filter change resets to page one - staying on page 7 of a result set that now
 * has two pages is the classic way to show an empty table to someone who just
 * narrowed a search.
 */

const SEARCH_DEBOUNCE_MS = 300;

/** Filters the toolbar owns. Sorting and paging are kept separately. */
export interface ScanFilterState {
  q: string;
  cms: string[];
  band: ScanListParams["band"];
  /** What the sites are for. The filter that separates shops from business cards. */
  kind: ScanListParams["kind"];
  outdated: boolean;
  abandoned: boolean;
  insecure: boolean;
  /** The malware check saw a named campaign marker, a defacement or hidden spam. */
  malwareConfirmed: boolean;
  /** The malware check saw something worth a look. Not an accusation. */
  malwareSuspected: boolean;
  hasEmail: boolean;
  hasCompany: boolean;
  dead: boolean;
  noHttps: boolean;
  noMobile: boolean;
}

export const EMPTY_FILTERS: ScanFilterState = {
  q: "",
  cms: [],
  band: [],
  kind: [],
  outdated: false,
  abandoned: false,
  insecure: false,
  malwareConfirmed: false,
  malwareSuspected: false,
  hasEmail: false,
  hasCompany: false,
  dead: false,
  noHttps: false,
  noMobile: false,
};

/** True when anything is narrowing the list, so the UI can offer a reset. */
export function hasActiveFilters(f: ScanFilterState): boolean {
  return (
    Boolean(f.q) ||
    f.cms.length > 0 ||
    (f.band?.length ?? 0) > 0 ||
    (f.kind?.length ?? 0) > 0 ||
    f.outdated ||
    f.abandoned ||
    f.insecure ||
    f.malwareConfirmed ||
    f.malwareSuspected ||
    f.hasEmail ||
    f.hasCompany ||
    f.dead ||
    f.noHttps ||
    f.noMobile
  );
}

/** Filter state + sort + page -> the query the API understands. */
export function toParams(
  filters: ScanFilterState,
  sort: ScanSort,
  order: "asc" | "desc",
  page: number,
  pageSize: number,
): ScanListParams {
  return {
    q: filters.q || undefined,
    cms: filters.cms.length ? filters.cms : undefined,
    band: filters.band?.length ? filters.band : undefined,
    kind: filters.kind?.length ? filters.kind : undefined,
    outdated: filters.outdated || undefined,
    abandoned: filters.abandoned || undefined,
    insecure: filters.insecure || undefined,
    // One column, two chips. Ticking both would ask for a verdict that is
    // two values at once, so the stronger one wins rather than returning
    // an empty list the operator cannot explain.
    malware: filters.malwareConfirmed ? "confirmed" : filters.malwareSuspected ? "suspected" : undefined,
    hasEmail: filters.hasEmail || undefined,
    hasCompany: filters.hasCompany || undefined,
    dead: filters.dead || undefined,
    // "No HTTPS" and "no mobile view" are the two findings a buyer understands
    // without explanation, so they are filters rather than something to read off
    // the detail panel one row at a time.
    https: filters.noHttps ? false : undefined,
    mobile: filters.noMobile ? false : undefined,
    sort,
    order,
    page,
    pageSize,
  };
}

export function useScansTable() {
  const [filters, setFilters] = useState<ScanFilterState>(EMPTY_FILTERS);
  const [qInput, setQInput] = useState("");
  const [sort, setSort] = useState<ScanSort>("checked");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [items, setItems] = useState<ScanListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<ScanFacets | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // A slow request for page 1 must not overwrite a fast one for page 2.
  const requestId = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (f.q === qInput ? f : { ...f, q: qInput }));
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [qInput]);

  const params = useMemo(
    () => toParams(filters, sort, order, page, pageSize),
    [filters, sort, order, page, pageSize],
  );

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const result = await listScans(params);
      if (id !== requestId.current) return;
      setItems(result.items);
      setTotal(result.total);
      setError(false);
    } catch {
      if (id !== requestId.current) return;
      setError(true);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [params]);

  const loadFacets = useCallback(async () => {
    try {
      setFacets(await scanFacets());
    } catch {
      // The chips are a convenience; the table works without their counts.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadFacets();
  }, [loadFacets]);

  /** Patch the filters and go back to page one. The only way filters change. */
  const patchFilters = useCallback((patch: Partial<ScanFilterState>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  }, []);

  const reset = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setQInput("");
    setPage(1);
  }, []);

  /** Click a column header: same column flips the direction, a new one starts. */
  const toggleSort = useCallback(
    (next: ScanSort) => {
      if (next === sort) setOrder((o) => (o === "asc" ? "desc" : "asc"));
      else {
        setSort(next);
        // Text sorts read naturally ascending; every number is "worst first".
        setOrder(next === "domain" || next === "company" ? "asc" : "desc");
      }
      setPage(1);
    },
    [sort],
  );

  const refresh = useCallback(() => {
    void load();
    void loadFacets();
  }, [load, loadFacets]);

  return {
    filters,
    patchFilters,
    reset,
    qInput,
    setQInput,
    sort,
    order,
    toggleSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    params,
    items,
    total,
    facets,
    loading,
    error,
    refresh,
  };
}
